import React, { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { Threebox } from 'threebox-plugin'
import {
  Button,
  Modal,
  Upload,
  Row,
  Col,
  Switch,
  Progress,
  Spin,
  Input
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import className from 'classnames'
import Dropzone from 'react-dropzone'
import { useTranslation } from 'react-i18next' // Added for translation support
import { uploadMedia } from '../../../../internal/List/images'
import SearchBox from './Search'

import 'mapbox-gl/dist/mapbox-gl.css'
import 'antd/dist/antd.css'
import './Map.css'

const { TextArea } = Input

// Map Style Toggle Control
class MapStyleToggleControl {
  constructor() {
    this._map = null
    this._container = null
    this._button = null
  }

  onAdd(map) {
    this._map = map
    this._container = document.createElement('div')
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
    this._container.style.cssText = `
      position: absolute;
      right: 10px;
      top: 10px;
      z-index: 1000;
      margin-right: 8em;
    `

    this._button = document.createElement('button')
    this._button.className = 'mapboxgl-ctrl-icon'
    this._button.type = 'button'
    this._button.title = 'Toggle Map Style'
    this._button.innerHTML =
      '<span class="mapboxgl-ctrl-icon" style="margin-left: 6px;margin-top: 6px;" aria-hidden="true">🗺️</span>'
    this._button.style.cssText = `
      width: 30px;
      height: 30px;
      background-color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 4px rgba(0,0,0,0.3);
    `

    // Add hover effect
    this._button.addEventListener('mouseenter', () => {
      this._button.style.backgroundColor = '#f0f0f0'
    })
    this._button.addEventListener('mouseleave', () => {
      this._button.style.backgroundColor = 'white'
    })

    this._button.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()
      if (this._map) {
        const currentStyle = this._map.getStyle().sprite
        const newStyle = currentStyle.includes('streets')
          ? 'mapbox://styles/mapbox/satellite-streets-v12'
          : 'mapbox://styles/mapbox/streets-v12'
        this._map.setStyle(newStyle)
      }
    })

    this._container.appendChild(this._button)
    return this._container
  }

  onRemove() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container)
    }
    this._map = null
    this._container = null
    this._button = null
  }
}

const Mapbox = ({
  mediaDat,
  onDropppedZip,
  onGeoLocation,
  onDeleteMarker,
  isMarkerBased,
  zipLoading,
  zipSizeCheck,
  isZip,
  onSwitchInteractionMode,
  switchInteractive
}) => {
  const { t } = useTranslation() // Initialize translation hook
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const [coordinates, setCoordinates] = useState([])
  const [selectedFeature, setSelectedFeature] = useState(null)
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12') // Add state for map style

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0)
  const [markerDescription, setMarkerDescription] = useState('')

  // Store marker data with format: [{text, longitude, latitude}, ...]
  const [markersData, setMarkersData] = useState([])

  // Styles for new components
  const sectionStyle = { marginBottom: '20px' }
  const headingStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '5px',
    textAlign: 'left'
  }
  const descriptionStyle = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px'
  }
  const modalInnerStyle = {
    padding: '15px',
    border: '1px dashed #d9d9d9',
    borderRadius: '4px'
  }
  const dropzoneTextStyle = { textAlign: 'center', marginTop: '10px' }
  const textAreaStyle = { marginTop: '15px', marginBottom: '15px' }

  // Refs to track the latest state values
  const selectedFeatureRef = useRef(null)
  const markersRef = useRef([])
  const popupBtnListenersRef = useRef({})
  const markersDataRef = useRef([])

  // Sync markersDataRef with markersData state
  useEffect(() => {
    markersDataRef.current = markersData || []
  }, [markersData])

  // Function to check if upload is enabled
  const isUploadEnabled = () => {
    return (
      markerDescription.trim() !== '' &&
      mediaDat && mediaDat.hasOwnProperty('ar_files')
    )
  }

  // Function to handle modal visibility
  const handleModalOpen = index => {
    setCurrentMarkerIndex(index)

    // Set description from stored marker data if available
    const existingData = markersData.find(m => m.index === index)
    setMarkerDescription((existingData && existingData.text) || '')

    setIsModalVisible(true)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
    clearModalFields()
  }

  // Clear fields after modal is closed
  const clearModalFields = () => {
    setMarkerDescription('')
  }

  // Handle description change
  const handleDescriptionChange = e => {
    setMarkerDescription(e.target.value)
  }

  // Handle finishing the upload - Store marker data in array
  const handleUpload = () => {
    // Get marker coordinates
    const markerCoords =
      markersRef.current &&
      markersRef.current[currentMarkerIndex] &&
      markersRef.current[currentMarkerIndex].marker.getLngLat()

    if (markerCoords) {
      // Create marker data object with format {text, longitude, latitude}
      const markerData = {
        index: currentMarkerIndex,
        text: markerDescription,
        longitude: markerCoords.lng,
        latitude: markerCoords.lat
      }

      // Create updated markers data
      const updatedMarkersData = [
        ...(markersDataRef.current || []),
        markerData
      ].sort((a, b) => b.index - a.index)

      // Update markersData state
      setMarkersData(updatedMarkersData)

      // Pass the updated markers data to parent component through props
      if (onGeoLocation) {
        onGeoLocation(updatedMarkersData)
      }
    }
    // Close the modal and clear fields
    setIsModalVisible(false)
    clearModalFields()
  }

  // Handle location selection from Search component
  const handleLocationSelect = location => {
    console.log('Location selected:', location)
    if (!location) return

    let lng, lat

    // Handle location data from Search component
    if (location.coordinates) {
      lat = location.coordinates.lat
      lng = location.coordinates.lng
    } else if (location.geometry && location.geometry.location) {
      lat = location.geometry.location.lat()
      lng = location.geometry.location.lng()
    } else {
      console.log('Invalid location data:', location)
      return
    }

    console.log('Flying to coordinates:', { lat, lng })

    // Ensure map is loaded before flying
    if (mapRef.current && mapRef.current.isStyleLoaded()) {
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 15,
        essential: true,
        duration: 3000, // Increased duration for smoother animation
        easing: t => {
          // Custom easing function for smoother animation
          return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
        }
      })

      // Add marker at the selected location
      addMarkerAtLocation([lng, lat])
    } else {
      // If map is not loaded, wait for it to load
      const checkMapLoaded = setInterval(() => {
        if (mapRef.current && mapRef.current.isStyleLoaded()) {
          clearInterval(checkMapLoaded)
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: 15,
            essential: true,
            duration: 3000,
            easing: t => {
              return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
            }
          })
          addMarkerAtLocation([lng, lat])
        }
      }, 100)
    }
  }

  // Function to add marker at a specific location
  const addMarkerAtLocation = async lngLat => {
    const newIndex = markersRef.current.length

    // Define marker colors and images
    const markerColors = ['red', 'blue', 'green', 'yellow']
    const markerImages = [
      'https://docs.mapbox.com/mapbox-gl-js/assets/washington-monument.jpg',
      'https://docs.mapbox.com/help/demos/custom-markers-gl-js/mapbox-icon.png',
      'https://docs.mapbox.com/mapbox-gl-js/assets/cat.png',
      'https://docs.mapbox.com/mapbox-gl-js/assets/dog-park.png'
    ]

    // Create marker element
    const el = document.createElement('div')
    el.className = 'custom-marker'
    el.style.backgroundImage = `url('${markerImages[
      newIndex % markerImages.length
    ] || markerImages[0]}')`
    el.style.backgroundSize = 'cover'
    el.style.width = '40px'
    el.style.height = '40px'
    el.style.borderRadius = '50%'
    el.style.cursor = 'pointer'
    el.style.border = `3px solid ${
      markerColors[newIndex % markerColors.length]
    }`

    // Create popup
    const popup = new mapboxgl.Popup({
      offset: 25,
      closeButton: true,
      closeOnClick: false,
      className: 'marker-popup',
      maxWidth: '300px'
    }).setHTML(createPopupContent(lngLat[0], lngLat[1], newIndex))

    // Create marker
    const marker = new mapboxgl.Marker({
      element: el,
      draggable: true
    })
      .setLngLat(lngLat)
      .setPopup(popup)
      .addTo(mapRef.current)

    // Store marker reference
    markersRef.current.push({
      marker,
      popup,
      id: newIndex,
      color: markerColors[newIndex % markerColors.length]
    })

    // Setup marker events
    setupMarkerEvents(marker, popup, newIndex)

    // Update coordinates state
    setCoordinates(prev => {
      const newCoords = [...prev]
      newCoords[newIndex] = `Marker ${newIndex +
        1}: Longitude: ${lngLat[0].toFixed(4)}, Latitude: ${lngLat[1].toFixed(
        4
      )}`
      return newCoords
    })
  }

  // Setup event listeners for popup buttons - IMPROVED VERSION
  const setupPopupButtonListeners = index => {
    setTimeout(() => {
      const uploadBtn = document.getElementById(`upload-btn-${index}`)
      if (uploadBtn) {
        // Remove any existing listeners to avoid duplicates
        const oldListener = popupBtnListenersRef.current[`btn-${index}`]
        if (oldListener) {
          uploadBtn.removeEventListener('click', oldListener)
        }

        // Add new listener with proper event handling
        const listener = e => {
          e.preventDefault()
          e.stopPropagation()
          handleModalOpen(index)
          return false // Prevent further propagation
        }

        uploadBtn.addEventListener('click', listener)

        // Save reference to the listener for cleanup
        popupBtnListenersRef.current[`btn-${index}`] = listener
      }
    }, 100) // Small delay to ensure DOM is ready
  }

  // Function to close all marker popups
  const closeAllPopups = () => {
    markersRef.current.forEach(markerObj => {
      if (markerObj.marker && markerObj.popup.isOpen()) {
        markerObj.marker.getPopup().remove()
      }
    })
  }

  // Function to create popup content with buttons
  const createPopupContent = (lng, lat, index) => `
    <div style="color: #333; font-weight: bold; position: relative; z-index: 10;">
      <h3 style={{ margin: '0 0 5px 0', color: (markersRef.current[index] && markersRef.current[index].color) || 'red' }}>
           Marker ${index}
      </h3
       <p style="margin: 0; font-size: 12px; color: #000;">
        Longitude: ${lng.toFixed(4)}<br>
        Latitude: ${lat.toFixed(4)}
      </p>
      <p style="margin: 5px 0 0; font-size: 12px; color: #666;">
        Drag me to update coordinates!
      </p>
      <div style="position: relative; z-index: 999; margin-top: 8px; display: flex; gap: 8px;">
        <button 
          id="upload-btn-${index}" 
          style="padding: 4px 8px; background-color: #1890ff; color: white; border: none; border-radius: 2px; cursor: pointer; position: relative; z-index: 1000; pointer-events: auto; display: flex; align-items: center; gap: 4px;"
          onclick="event.stopPropagation(); return false;"
        >
          Upload Assets
        </button>
        <button 
          id="delete-btn-${index}" 
          style="padding: 4px 8px; background-color: #ff4d4f; color: white; border: none; border-radius: 2px; cursor: pointer; position: relative; z-index: 1100; pointer-events: auto; display: flex; align-items: center; gap: 4px;"
          onclick="event.stopPropagation(); return false;"
        >
           Delete
        </button>
      </div>
    </div>
  `

  // Function to setup marker events
  const setupMarkerEvents = (marker, popup, index) => {
    marker.on('dragend', () => {
      const lngLat = marker.getLngLat()
      setCoordinates(prev => {
        const newCoords = [...prev]
        newCoords[index] = `Marker ${index +
          1}: Longitude: ${lngLat.lng.toFixed(
          4
        )}, Latitude: ${lngLat.lat.toFixed(4)}`
        return newCoords
      })
      popup.setHTML(createPopupContent(lngLat.lng, lngLat.lat, index))
      if (popup.isOpen()) {
        setupPopupButtonListeners(index)
      }
    })

    marker.getElement().addEventListener('click', e => {
      e.stopPropagation()
      if (selectedFeatureRef.current) {
        mapRef.current.setFeatureState(selectedFeatureRef.current, {
          selected: false
        })
        setSelectedFeature(null)
      }
      closeAllPopups()
      marker.togglePopup()
      if (popup.isOpen()) {
        setupPopupButtonListeners(index)
      }
    })

    // Add delete button event listener
    popup.on('open', () => {
      const deleteBtn = document.getElementById(`delete-btn-${index}`)
      if (deleteBtn) {
        // Remove any existing listeners to avoid duplicates
        const newDeleteBtn = deleteBtn.cloneNode(true)
        deleteBtn.parentNode.replaceChild(newDeleteBtn, deleteBtn)

        newDeleteBtn.addEventListener('click', e => {
          e.preventDefault()
          e.stopPropagation()

          // Remove marker from map
          marker.remove()

          // Remove from markers array
          markersRef.current = markersRef.current.filter(m => m.id !== index)

          // Update coordinates state
          setCoordinates(prev => prev.filter((_, i) => i !== index))

          // Update markers data
          setMarkersData(prev => prev.filter(m => m.index !== index))

          // Call onDeleteMarker with the deleted index
          if (onDeleteMarker) {
            onDeleteMarker(index)
          }

          // Close popup
          popup.remove()
        })
      }
    })
  }

  useEffect(() => {
    mapboxgl.accessToken =
      'pk.eyJ1IjoibWFwYm94LXJhYW5hbiIsImEiOiJjbTh4eGZpbmwwOHdzMnNwbHlvaTFyNWdxIn0.uy0dfRpvkNH-r5R1KvtnWQ'

    // Custom Add Marker Control
    class AddMarkerControl {
      constructor() {
        this._map = null
        this._container = null
        this._button = null
      }

      onAdd(map) {
        this._map = map
        this._container = document.createElement('div')
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
        this._container.style.cssText = `
          position: absolute;
          right: 10px;
          top: 10px;
          z-index: 1000;
          margin-right: 4em;
        `

        this._button = document.createElement('button')
        this._button.className = 'mapboxgl-ctrl-icon'
        this._button.type = 'button'
        this._button.title = 'Add Marker'
        this._button.innerHTML =
          '<span class="mapboxgl-ctrl-icon" style="margin-left: 6px;margin-top: 6px;" aria-hidden="true">📍</span>'
        this._button.style.cssText = `
          width: 30px;
          height: 30px;
          background-color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 4px rgba(0,0,0,0.3);
        `

        this._button.addEventListener('click', e => {
          e.preventDefault()
          e.stopPropagation()
          const center = map.getCenter()
          const newIndex = markersRef.current.length
          addMarkerAtLocation([center.lng, center.lat])
        })

        this._container.appendChild(this._button)
        return this._container
      }

      onRemove() {
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container)
        }
        this._map = null
        this._container = null
        this._button = null
      }
    }

    // Initialize map with current style
    if (!mapRef.current) {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center: [-74.0066, 40.7135],
        zoom: 15.5,
        pitch: 45,
        bearing: -17.6,
        antialias: true,
        attributionControl: false
      })

      mapRef.current = map

      // Wait for the map to be fully loaded
      map.on('load', () => {
        // Add geolocate control for current location tracking
        map.addControl(
          new mapboxgl.GeolocateControl({
            positionOptions: {
              enableHighAccuracy: true
            },
            trackUserLocation: true,
            showUserHeading: true
          }),
          'top-right'
        )

        // Add the custom control to the map
        map.addControl(new AddMarkerControl(), 'top-right')

        // Add the map style toggle control
        map.addControl(new MapStyleToggleControl(), 'top-right')

        // Add 3D buildings
        const layers = map.getStyle().layers
        let labelLayerId = null

        // Find label layer
        for (let i = 0; i < layers.length; i++) {
          const layer = layers[i]
          if (
            layer &&
            layer.type === 'symbol' &&
            layer.layout &&
            layer.layout['text-field']
          ) {
            labelLayerId = layer.id
            break
          }
        }

        if (labelLayerId) {
          map.addLayer(
            {
              id: 'add-3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 15,
              paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  15,
                  0,
                  15.05,
                  ['get', 'height']
                ],
                'fill-extrusion-base': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  15,
                  0,
                  15.05,
                  ['get', 'min_height']
                ],
                'fill-extrusion-opacity': 0.6
              }
            },
            labelLayerId
          )
        }

        // Add airports source and layer
        map.addSource('airports', {
          type: 'vector',
          url: 'mapbox://mapbox.04w69w5j',
          promoteId: 'abbrev'
        })

        map.addLayer({
          id: 'airport',
          source: 'airports',
          'source-layer': 'ne_10m_airports',
          type: 'circle',
          paint: {
            'circle-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#f00',
              '#4264fb'
            ],
            'circle-radius': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              6,
              ['boolean', ['feature-state', 'highlight'], false],
              6,
              4
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        })

        // Add custom 3D model using Threebox
        map.addLayer({
          id: 'custom-threebox-model',
          type: 'custom',
          renderingMode: '3d',
          onAdd: function() {
            window.tb = new Threebox(map, map.getCanvas().getContext('webgl'), {
              defaultLights: true
            })
            const scale = 3.2
            const options = {
              obj:
                'https://docs.mapbox.com/mapbox-gl-js/assets/metlife-building.gltf',
              type: 'gltf',
              scale: { x: scale, y: scale, z: 2.7 },
              units: 'meters',
              rotation: { x: 90, y: -90, z: 0 }
            }

            window.tb.loadObj(options, model => {
              model.setCoords([-74.0066, 40.7135])
              model.setRotation({ x: 0, y: 0, z: 241 })
              window.tb.add(model)
            })
          },
          render: function() {
            window.tb && window.tb.update()
          }
        })

        // Setup interactions for airport features
        map.on('click', 'airport', e => {
          if (selectedFeatureRef.current) {
            map.setFeatureState(selectedFeatureRef.current, {
              selected: false
            })
          }

          if (e.features.length > 0) {
            const feature = {
              id: e.features[0].id,
              source: 'airports',
              sourceLayer: 'ne_10m_airports'
            }

            map.setFeatureState(feature, { selected: true })
            setSelectedFeature(e.features[0])

            // Close all marker popups when selecting an airport
            closeAllPopups()
          }
        })

        // Clicking elsewhere on the map deselects the airport and marker
        map.on('click', e => {
          // Check if the click was on an airport
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['airport']
          })
          if (features.length === 0) {
            // Deselect airport feature if one is selected
            if (selectedFeatureRef.current) {
              map.setFeatureState(selectedFeatureRef.current, {
                selected: false
              })
              setSelectedFeature(null)
            }

            // Check if the click was directly on the map (not on a marker)
            // If so, close all marker popups
            const markerElements = document.querySelectorAll('.custom-marker')
            let clickedOnMarker = false

            for (let i = 0; i < markerElements.length; i++) {
              const rect = markerElements[i].getBoundingClientRect()
              if (
                e.originalEvent.clientX >= rect.left &&
                e.originalEvent.clientX <= rect.right &&
                e.originalEvent.clientY >= rect.top &&
                e.originalEvent.clientY <= rect.bottom
              ) {
                clickedOnMarker = true
                break
              }
            }

            if (!clickedOnMarker) {
              closeAllPopups()
            }
          }
        })

        // Hover effects for airports
        map.on('mouseenter', 'airport', e => {
          map.getCanvas().style.cursor = 'pointer'
          if (e.features.length > 0) {
            const feature = {
              id: e.features[0].id,
              source: 'airports',
              sourceLayer: 'ne_10m_airports'
            }
            map.setFeatureState(feature, { highlight: true })
          }
        })

        map.on('mouseleave', 'airport', e => {
          map.getCanvas().style.cursor = ''
          if (e.features.length > 0) {
            const feature = {
              id: e.features[0].id,
              source: 'airports',
              sourceLayer: 'ne_10m_airports'
            }
            map.setFeatureState(feature, { highlight: false })
          }
        })
      })
    } else {
      // Update existing map style
      mapRef.current.setStyle(mapStyle)
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [mapStyle])

  // Sync Ref with State for selected feature
  useEffect(() => {
    selectedFeatureRef.current = selectedFeature
      ? {
          id: selectedFeature.id,
          source: 'airports',
          sourceLayer: 'ne_10m_airports'
        }
      : null
  }, [selectedFeature])

  console.log('markersData log', markersData)

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <SearchBox onLocationSelect={handleLocationSelect} />
      <div
        ref={mapContainerRef}
        id="map"
        style={{ width: '100%', height: '100%' }}
      ></div>

      {/* Selected airport feature info panel */}
      {selectedFeature && (
        <div
          style={{
            position: 'absolute',
            right: '10px',
            top: '10px',
            width: '230px',
            padding: '10px',
            color: '#1a2224',
            fontSize: '12px',
            lineHeight: '20px',
            fontFamily: 'sans-serif'
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '10px',
              borderRadius: '3px',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)'
            }}
          >
            <h3 style={{ margin: '0 0 5px 0' }}>
              {selectedFeature.properties.name}
            </h3>
            <hr style={{ border: '1px solid #eee', margin: '5px 0' }} />
            <ul style={{ padding: '0 0 0 15px', margin: '5px 0' }}>
              {selectedFeature.properties &&
                Object.entries(selectedFeature.properties)
                  .filter(([key]) => key !== 'name') // Skip name as it's already shown
                  .map(([key, value], idx) => (
                    <li key={idx}>
                      <b>{key}</b>: {value.toString()}
                    </li>
                  ))}
            </ul>
            <button
              onClick={() => {
                if (selectedFeatureRef.current) {
                  mapRef.current.setFeatureState(selectedFeatureRef.current, {
                    selected: false
                  })
                }
                setSelectedFeature(null)
              }}
              style={{
                background: '#4264fb',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '3px',
                cursor: 'pointer',
                marginTop: '5px'
              }}
            >
              {t('v3:close')}
            </button>
          </div>
        </div>
      )}

      {/* Upload Assets Modal */}
      <Modal
        title={`Upload Assets for Marker ${currentMarkerIndex + 1}`}
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            {t('v4:cancel')}
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            disabled={!isUploadEnabled()}
          >
            {t('buttons:upload')}
          </Button>
        ]}
      >
        {/* Description Text Area */}
        <Row style={sectionStyle}>
          <Col span={24}>
            <h3 style={headingStyle}>{t('Marker Description')}</h3>
            <p style={descriptionStyle}>
              {t('Enter details about this location (max 3 lines)')}
            </p>
            <TextArea
              rows={3}
              placeholder="Enter description here..."
              value={markerDescription}
              onChange={handleDescriptionChange}
              style={textAreaStyle}
              maxLength={300}
            />
          </Col>
        </Row>

        <Row className="mt-4" style={sectionStyle}>
          <Col span={24}>
            <h3 className="upload-modal__text" style={headingStyle}>
              {t('Interactive Mode')}
            </h3>
            <p style={descriptionStyle}>
              {t('(Enable or disable 3D interaction)')}
            </p>
            <Switch
              checked={switchInteractive}
              onChange={onSwitchInteractionMode}
            />
          </Col>
        </Row>

        {/* AR Content Section - shown for both modes based on interactive toggle */}
        <Row className="mt-4" style={sectionStyle}>
          <Col span={24}>
            <h3 className="upload-modaltext" style={headingStyle}>
              {switchInteractive
                ? t('Interactive AR Content')
                : t('Non Interactive AR Content')}
            </h3>
            <p style={descriptionStyle}>
              {switchInteractive
                ? t('v2:_3d_files_mp4_ar')
                : t('v2:_3d_files_mp4')}
            </p>
            <div className="upload-modalinner" style={modalInnerStyle}>
              <div className="upload-modal__dropzone">
                <Dropzone
                  accept={[
                    'application/zip',
                    'application/x-zip-compressed',
                    'video/*'
                  ]}
                  maxFiles={1}
                  maxSize={60000000}
                  onDrop={onDropppedZip}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                      className={className('upload-dropzone', {
                        'upload-dropzone_active': isDragActive
                      })}
                    >
                      <Spin size="large" spinning={zipLoading}>
                        <div className="upload-dropzonetext-wrapper">
                          <img src={uploadMedia} alt="" />
                          <p
                            className="upload-dropzonetext"
                            style={dropzoneTextStyle}
                          >
                            <span>{t('v5:upload_3d_zip')}</span> <br />
                            {t('v2:drag_n_drop')}
                          </p>
                          <br />
                          {zipSizeCheck && (
                            <em style={{ color: 'red' }}>
                              {t('v2:zip_size_should_not_exceed')}
                            </em>
                          )}
                          {!zipLoading && mediaDat.hasOwnProperty('ar_files') && (
                            <div>
                              <Progress
                                type="circle"
                                percent={100}
                                width={30}
                              />
                              <p
                                className="upload-dropzone__text"
                                style={dropzoneTextStyle}
                              >
                                {isZip
                                  ? t('v2:content_attached')
                                  : t('v2:video_attached')}
                              </p>
                            </div>
                          )}
                        </div>
                      </Spin>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          </Col>
        </Row>
      </Modal>
    </div>
  )
}

export default Mapbox
